import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { motionProfileFor, profileFor, type MotionProfile, type QualityProfile, type QualityTier } from '../lib/qualityProfile.js';

/**
 * Device capability, injected by the host application.
 *
 * This package deliberately does not probe the environment itself. The app
 * already resolves WebGL support, motion preference, viewport and device tier
 * once, and the portals depend on that same decision to suppress accents. Two
 * independent detectors would inevitably disagree.
 *
 * The host wires this up once in its provider tree; scenes consume the derived
 * quality and motion profiles.
 */

export interface SceneCapability {
  /** WebGL is available and the tier is not `off`. */
  enabled: boolean;
  tier: QualityTier;
  prefersReducedMotion: boolean;
  isTouch: boolean;
  /** Prefer the reduced-detail asset variant. */
  preferMobileAssets: boolean;
  pixelRatio: number;
  /** Called by the performance monitor when frames are consistently late. */
  reportPoorPerformance: () => void;
}

interface SceneCapabilityValue extends SceneCapability {
  quality: QualityProfile;
  motion: MotionProfile;
}

const FALLBACK: SceneCapabilityValue = {
  enabled: false,
  tier: 'low',
  prefersReducedMotion: true,
  isTouch: false,
  preferMobileAssets: true,
  pixelRatio: 1,
  reportPoorPerformance: () => {},
  quality: profileFor('low'),
  motion: motionProfileFor(true),
};

const CapabilityCtx = createContext<SceneCapabilityValue>(FALLBACK);

export function SceneCapabilityProvider({
  value,
  children,
}: {
  value: SceneCapability;
  children: ReactNode;
}): JSX.Element {
  const resolved = useMemo<SceneCapabilityValue>(
    () => ({
      ...value,
      quality: {
        ...profileFor(value.tier),
        // The host's pixel-ratio cap is authoritative - it accounts for the
        // in-app motion toggle and any user override.
        maxPixelRatio: Math.min(profileFor(value.tier).maxPixelRatio, value.pixelRatio),
      },
      motion: motionProfileFor(value.prefersReducedMotion),
    }),
    [value],
  );

  return <CapabilityCtx.Provider value={resolved}>{children}</CapabilityCtx.Provider>;
}

/**
 * Capability for scene code. Falls back to a disabled, reduced-motion profile
 * when no provider is present, so a scene rendered in a test or in isolation
 * degrades instead of throwing.
 */
export function useSceneCapability(): SceneCapabilityValue {
  return useContext(CapabilityCtx);
}
